// src/context/AuthContext.jsx

import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import {
  isTokenValid,
  clearAuthStorage,
  updateLastActivity,
  getLastActivityMs,
  isSessionActive,
  shouldRefreshTokenSoon,
  getAllowedBranchIds,
  isBranchAllowed,
} from '../utils/tokenUtils';
import axios, { refreshAccessToken } from '../axios';

const AuthContext = createContext(null);

const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click', 'input'];

const PUBLIC_PATHS = ['/', '/login', '/register', '/forgot-password', '/reset-password'];

const isPublicRoute = (path) => {
  const p = (path || window.location.pathname || '/').toLowerCase();
  if (p === '/' || p === '') return true;
  return PUBLIC_PATHS.some(pp => p === pp || p.startsWith(pp + '/'));
};

const CHECK_EVERY_MS = 15000;
const ACTIVITY_THROTTLE_MS = 2000;

export const AuthProvider = ({ children }) => {
  const [permissions, setPermissions] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('permissions') || '[]');
    } catch {
      return [];
    }
  });
  const [allowedBranchIds, setAllowedBranchIds] = useState(() => getAllowedBranchIds());
  // مالك المنشأة يملك كل الصلاحيات (نفس قاعدة السيرفر)
  const [isOwner, setIsOwner] = useState(() => localStorage.getItem('isOwner') === 'true');

  const isLoggingOutRef = useRef(false);
  const lastActivityLocalRef = useRef(0);

  // ───────────────────────── تسجيل الخروج ─────────────────────────
  const revokeRefreshTokenOnServer = async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return;
    try {
      await axios.post('/User/logout', { refreshToken }, { _skipAuthCheck: true });
    } catch { /* تجاهل */ }
  };

  // force = true عند ضغط المستخدم زر الخروج (يعمل من أي صفحة)
  const forceLogout = useCallback(async (force = false) => {
    if (isLoggingOutRef.current) return;
    if (!force && isPublicRoute()) return;
    isLoggingOutRef.current = true;

    await revokeRefreshTokenOnServer();

    setPermissions([]);
    setIsOwner(false);
    localStorage.removeItem('isOwner');
    clearAuthStorage();
    window.location.href = '/login';
  }, []);

  // ───────────────────────── فحص الجلسة ─────────────────────────
  // يعمل دائمًا، ويتجاهل الصفحات العامة وقت الفحص نفسه
  // (هكذا تبدأ المراقبة تلقائيًا بعد الانتقال من صفحة الدخول للوحة التحكم)
  const checkSession = useCallback(async () => {
    if (isLoggingOutRef.current || isPublicRoute()) return;

    const token = localStorage.getItem('token');
    if (!token) return forceLogout();

    if (!getLastActivityMs()) updateLastActivity();

    if (!isSessionActive()) return forceLogout();

    if (!isTokenValid(token) || shouldRefreshTokenSoon(token)) {
      try {
        // ✅ نفس طلب التجديد المشترك مع axios — لا يُرسل الـ Refresh Token مرتين أبدًا
        await refreshAccessToken();
      } catch {
        const t = localStorage.getItem('token');
        if (!t || !isTokenValid(t)) forceLogout();
      }
    }
  }, [forceLogout]);

  const handleUserActivity = useCallback(() => {
    if (isLoggingOutRef.current || isPublicRoute()) return;
    if (!localStorage.getItem('token')) return;

    const now = Date.now();
    if (now - lastActivityLocalRef.current < ACTIVITY_THROTTLE_MS) return;
    lastActivityLocalRef.current = now;

    updateLastActivity();

    const token = localStorage.getItem('token');
    if (token && shouldRefreshTokenSoon(token)) {
      refreshAccessToken().catch(() => {});
    }
  }, []);

  // ───────────────────────── المراقبة ─────────────────────────
  useEffect(() => {
    const onBranchesStorage = () => setAllowedBranchIds(getAllowedBranchIds());

    // تسجيل الخروج من تبويب آخر
    const onTokenStorage = (e) => {
      if (e.key === 'token' && !e.newValue && !isPublicRoute()) {
        isLoggingOutRef.current = false;
        clearAuthStorage();
        window.location.href = '/login';
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkSession();
    };

    ACTIVITY_EVENTS.forEach(evt => document.addEventListener(evt, handleUserActivity, { passive: true }));
    window.addEventListener('storage', onBranchesStorage);
    window.addEventListener('storage', onTokenStorage);
    document.addEventListener('visibilitychange', onVisibility);

    checkSession();
    const interval = setInterval(checkSession, CHECK_EVERY_MS);

    return () => {
      ACTIVITY_EVENTS.forEach(evt => document.removeEventListener(evt, handleUserActivity));
      window.removeEventListener('storage', onBranchesStorage);
      window.removeEventListener('storage', onTokenStorage);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(interval);
    };
  }, [checkSession, handleUserActivity]);

  // ───────────────────────── الصلاحيات ─────────────────────────
  // ⚠️ هذا لإخفاء الأزرار فقط — الحماية الفعلية في السيرفر ([RequirePermission])
  const hasPermission = (requiredPermission) => {
    if (!requiredPermission || isOwner) return true;
    return permissions.includes(requiredPermission);
  };

  const hasAnyPermission = (requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0 || isOwner) return true;
    return requiredPermissions.some(perm => permissions.includes(perm));
  };

  const hasBranchAccess = (branchId) => {
    if (branchId == null) return false;
    return isBranchAllowed(branchId);
  };

  // تُستدعى من صفحة الدخول بعد النجاح
  const setAuthData = (data) => {
    if (data && data.permissions) {
      setPermissions(data.permissions);
      localStorage.setItem('permissions', JSON.stringify(data.permissions));
    }
    const owner = data?.isOwner === true;
    setIsOwner(owner);
    if (owner) localStorage.setItem('isOwner', 'true'); else localStorage.removeItem('isOwner');
    setAllowedBranchIds(getAllowedBranchIds());
    isLoggingOutRef.current = false;
    updateLastActivity();
    lastActivityLocalRef.current = Date.now();
  };

  const logout = useCallback(() => forceLogout(true), [forceLogout]);

  return (
    <AuthContext.Provider value={{
      permissions,
      isOwner,
      allowedBranchIds,
      hasPermission,
      hasAnyPermission,
      hasBranchAccess,
      setAuthData,
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export const PermissionGuard = ({ permission, children, fallback = null }) => {
  const { hasPermission } = useAuth();
  if (hasPermission(permission)) {
    return children;
  }
  return fallback;
};